import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X, User, Shield, KeyRound, ClipboardCheck, AlertTriangle,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import {
  USER_PROVISIONING_CAPABILITY,
  EMAIL_REGEX, USER_ROLES,
} from '../../lib/adminUsersApi';

// ---------------------------------------------------------------------------
// UserCreationWizard — pure UI preview scaffold, fail-closed
//
// USER_PROVISIONING_CAPABILITY = 'unavailable' in all runtimes.
// The wizard is an interactive preview ONLY: it performs zero HTTP requests,
// collects no passwords, and creates no accounts.
//
// Real provisioning is deferred to USER-PROVISIONING-CONTRACT-0001.
// ---------------------------------------------------------------------------

type UserRole = (typeof USER_ROLES)[number];

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface FormState {
  name: string;
  email: string;
  role: UserRole;
}

const emptyForm: FormState = {
  name: '',
  email: '',
  role: 'farmer',
};

const STEP_COUNT = 5;
const STEP_ICONS = [User, Shield, KeyRound, ClipboardCheck, AlertTriangle];

// ---------------------------------------------------------------------------
// Focusable-element selector — queried fresh on every Tab event
// ---------------------------------------------------------------------------
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusableDescendants(el: HTMLElement): HTMLElement[] {
  const nodes = el.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  return Array.from(nodes).filter(
    (n) => !(n.hidden || n.getAttribute('aria-hidden') === 'true' || n.offsetParent === null),
  );
}

const UserCreationWizard: React.FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation(['admin_users', 'common']);

  // P2-1 R6: Exhaustive typed role-label mapping.
  // Every role in USER_ROLES has exactly one static translation key.
  // No fallback — an unsupported role is a compile-time error.
  const roleLabels: Record<UserRole, string> = {
    superadmin: t('admin_users:roles.superadmin'),
    coop_admin: t('admin_users:roles.coop_admin'),
    farmer: t('admin_users:roles.farmer'),
    technician: t('admin_users:roles.technician'),
  };

  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>({ ...emptyForm });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // P2-1 R6.4: currentStepRef synchronised via a dedicated [step] effect
  // so the focus-trap effect never reads step from its own closure.
  const currentStepRef = useRef(step);

  useEffect(() => {
    currentStepRef.current = step;
  }, [step]);

  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  // P2-1 R4: Direct refs for focus management — avoids querySelector with useId values
  const nameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  // P2-R5 / P2-3 R6: Per-step focus targets — ensures focus lands on a real
  // heading (nameable element), never on a generic div or the Close button.
  const roleSelectRef = useRef<HTMLSelectElement>(null);
  const accessHeadingRef = useRef<HTMLHeadingElement>(null);
  const reviewHeadingRef = useRef<HTMLHeadingElement>(null);
  const conclusionHeadingRef = useRef<HTMLHeadingElement>(null);

  // Stable IDs for label ↔ control association (P2-5)
  const nameId = useId();
  const emailId = useId();
  const roleId = useId();

  // Stable error IDs for aria-describedby (P2-1)
  const nameErrorId = `${nameId}-error`;
  const emailErrorId = `${emailId}-error`;

  // P2-3 R6: Stable IDs for heading ↔ panel association
  const securityHeadingId = useId();
  const reviewHeadingId = useId();

  // ------------------------------------------------------------------
  // P2-R5: Per-step focus target — focuses the preferred element in the
  // newly active panel, never the Close button in the dialog header.
  // ------------------------------------------------------------------
  const focusCurrentStep = useCallback((targetStep: number) => {
    switch (targetStep) {
      case 0:
        nameInputRef.current?.focus();
        break;
      case 1:
        roleSelectRef.current?.focus();
        break;
      case 2:
        accessHeadingRef.current?.focus();
        break;
      case 3:
        reviewHeadingRef.current?.focus();
        break;
      case 4:
        conclusionHeadingRef.current?.focus();
        break;
    }
  }, []);

  // ------------------------------------------------------------------
  // P2-3 R4: Ref tracking the latest onClose — makes close stable even when
  // the parent supplies an unstable (inline) callback across rerenders.
  // ------------------------------------------------------------------
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // ------------------------------------------------------------------
  // Deterministic reset — clears every piece of user-entered state
  // ------------------------------------------------------------------
  const resetState = useCallback(() => {
    setStep(0);
    setForm({ ...emptyForm });
    setErrors({});
  }, []);

  // ------------------------------------------------------------------
  // Close helper — resets synchronously before calling the latest onClose
  // ------------------------------------------------------------------
  const close = useCallback(() => {
    resetState();
    onCloseRef.current();
  }, [resetState]);

  // ------------------------------------------------------------------
  // P2-3: Lock document scroll while dialog is open
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // ------------------------------------------------------------------
  // Focus trap — open / close / step-transition (P2-2, P2-3)
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;

    const dialog = dialogRef.current;
    if (!dialog) return;

    // A. Save the element that triggered the dialog (restore on close)
    if (document.activeElement instanceof HTMLElement) {
      restoreRef.current = document.activeElement;
    }

    // B. Move focus to the per-step target via the stable callback,
    // reading the latest step from currentStepRef (no direct step
    // closure so the effect does not need step in its deps).
    const moveFocusIn = () => {
      focusCurrentStep(currentStepRef.current);

      // Fallback: if the preferred target did not receive focus
      // (e.g. because it is not yet in the DOM), pick the first
      // focusable descendant or the dialog itself.
      const active = document.activeElement;
      if (active && dialog.contains(active)) return;

      const focusable = getFocusableDescendants(dialog);
      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        dialog.focus();
      }
    };
    moveFocusIn();

    // C / D. Tab / Shift+Tab looping
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusable = getFocusableDescendants(dialog);

      if (focusable.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      // If focus is outside the dialog, recover it
      if (!dialog.contains(active)) {
        e.preventDefault();
        if (e.shiftKey) {
          last.focus();
        } else {
          first.focus();
        }
        return;
      }

      if (e.shiftKey) {
        if (active === first || active === dialog) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || active === dialog) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    // Close on Escape
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keydown', handleEsc);

      // F. Restore focus to the trigger element on close
      if (restoreRef.current && restoreRef.current.isConnected) {
        restoreRef.current.focus();
      }
      restoreRef.current = null;
    };
  }, [isOpen, close, focusCurrentStep]);

  // ------------------------------------------------------------------
  // E. Step-transition focus management
  //
  // P2-R5: Uses per-step direct refs (focusCurrentStep) so focus lands
  // inside the newly active panel — never on the Close button.
  // Only fires on actual step changes, not on initial mount or
  // same-step rerenders.
  // ------------------------------------------------------------------
  const prevStepRef = useRef(step);

  useEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    // Only act on actual step transitions, not the initial render
    if (step === prevStepRef.current) return;

    focusCurrentStep(step);
  }, [step, isOpen, focusCurrentStep]);

  // Keep the ref in sync after the effect runs
  useEffect(() => {
    prevStepRef.current = step;
  }, [step]);

  // ------------------------------------------------------------------
  // Step labels
  // ------------------------------------------------------------------
  const stepLabels = [
    t('admin_users:wizard.step_identity'),
    t('admin_users:wizard.step_role'),
    t('admin_users:wizard.step_access'),
    t('admin_users:wizard.step_review'),
    t('admin_users:wizard.step_conclusion'),
  ];

  // ------------------------------------------------------------------
  // Capability
  // ------------------------------------------------------------------
  const isCapabilityUnavailable = USER_PROVISIONING_CAPABILITY === 'unavailable';

  if (!isOpen) return null;

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // ------------------------------------------------------------------
  // Validation — only identity fields (step 0) are validated
  // ------------------------------------------------------------------
  const hasNameError = (): boolean => form.name.trim().length < 2;
  const hasEmailError = (): boolean => !EMAIL_REGEX.test(form.email.trim());

  const validateStep = (s: number): boolean => {
    const e: Record<string, string> = {};
    if (s === 0) {
      if (hasNameError()) e.name = t('admin_users:wizard.err_name');
      if (hasEmailError()) e.email = t('admin_users:wizard.err_email');
    }
    setErrors(e);

    if (Object.keys(e).length > 0) {
      // P2-1 R4: Focus the first invalid field via direct ref (no querySelector with useId)
      if (e.name) {
        nameInputRef.current?.focus();
      } else if (e.email) {
        emailInputRef.current?.focus();
      }
      return false;
    }
    return true;
  };

  const next = () => {
    if (validateStep(step)) setStep((s) => Math.min(STEP_COUNT - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  // ------------------------------------------------------------------
  // Shared style tokens
  // ------------------------------------------------------------------
  const inputCls =
    'w-full p-2.5 border border-[var(--border-light)] rounded-lg bg-[var(--surface)] text-[var(--text-primary)] focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent';

  const labelCls = 'block text-sm font-medium text-[var(--text-secondary)] mb-1';

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ucw-dialog-title"
      tabIndex={-1}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 outline-none"
      data-testid="user-creation-wizard-overlay"
    >
      <div
        className="bg-[var(--surface)] rounded-xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col shadow-xl"
        data-testid="user-creation-wizard"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-light)]">
          <h2 id="ucw-dialog-title" className="text-heading-3 text-[var(--text-primary)]" data-testid="ucw-title">
            {t('admin_users:wizard.title')}
          </h2>
          <button
            onClick={close}
            className="p-1 rounded-lg hover:bg-[var(--gray-100)]"
            data-testid="ucw-close"
            aria-label={t('common:close')}
          >
            <X className="w-5 h-5 text-[var(--text-tertiary)]" aria-hidden="true" focusable="false" />
          </button>
        </div>

        {/* Stepper — P2-4: accessible on mobile via sr-only */}
        <ol
          className="flex items-center px-6 py-4 gap-2 border-b border-[var(--border-light)] list-none"
          aria-label={t('admin_users:wizard.steps_label')}
        >
          {stepLabels.map((label, i) => {
            const Icon = STEP_ICONS[i];
            const active = i === step;
            const done = i < step;
            return (
              <li
                key={label}
                className="flex items-center gap-2 flex-1 last:flex-none"
                aria-current={active ? 'step' : undefined}
                data-testid={`ucw-step-${i}`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm shrink-0 ${
                    active
                      ? 'bg-[var(--color-primary)] text-white'
                      : done
                        ? 'bg-[var(--color-success)]/15 text-[var(--color-success)]'
                        : 'bg-[var(--gray-100)] text-[var(--text-tertiary)]'
                  }`}
                >
                  <Icon className="w-4 h-4" aria-hidden="true" focusable="false" />
                </div>
                <span
                  className={`text-xs sr-only sm:not-sr-only ${
                    active ? 'text-[var(--color-primary)] font-medium' : 'text-[var(--text-secondary)]'
                  }`}
                >
                  {label}
                </span>
                {i < stepLabels.length - 1 && <div className="flex-1 h-px bg-[var(--border-light)]" aria-hidden="true" />}
              </li>
            );
          })}
        </ol>

        {/* Provisioning-unavailable banner — shown on all steps except conclusion */}
        {isCapabilityUnavailable && step < STEP_COUNT - 1 && (
          <div
            className="mx-6 mt-4 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm"
            data-testid="ucw-provisioning-unavailable-banner"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">{t('admin_users:wizard.provisioning_unavailable_title')}</div>
              <div>{t('admin_users:wizard.provisioning_unavailable_msg')}</div>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          {/* Step 0: Identity */}
          {step === 0 && (
            <div className="space-y-4" data-testid="ucw-panel-identity">
              <div>
                <label className={labelCls} htmlFor={nameId}>{t('admin_users:wizard.name')}</label>
                <input
                  ref={nameInputRef}
                  id={nameId}
                  type="text"
                  className={inputCls}
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder={t('admin_users:wizard.name_ph')}
                  data-testid="ucw-name"
                  autoComplete="name"
                  aria-invalid={errors.name ? true : undefined}
                  aria-describedby={errors.name ? nameErrorId : undefined}
                />
                {errors.name && (
                  <p id={nameErrorId} role="alert" className="text-[var(--color-danger)] text-xs mt-1" data-testid="ucw-err-name">
                    {errors.name}
                  </p>
                )}
              </div>
              <div>
                <label className={labelCls} htmlFor={emailId}>{t('admin_users:wizard.email')}</label>
                <input
                  ref={emailInputRef}
                  id={emailId}
                  type="email"
                  className={inputCls}
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                  placeholder={t('admin_users:wizard.email_ph')}
                  data-testid="ucw-email"
                  autoComplete="email"
                  aria-invalid={errors.email ? true : undefined}
                  aria-describedby={errors.email ? emailErrorId : undefined}
                />
                {errors.email && (
                  <p id={emailErrorId} role="alert" className="text-[var(--color-danger)] text-xs mt-1" data-testid="ucw-err-email">
                    {errors.email}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 1: Role & organization */}
          {step === 1 && (
            <div className="space-y-4" data-testid="ucw-panel-role">
              <div>
                <label className={labelCls} htmlFor={roleId}>{t('admin_users:wizard.role')}</label>
                <select
                  ref={roleSelectRef}
                  id={roleId}
                  className={inputCls}
                  value={form.role}
                  onChange={(e) => setField('role', e.target.value as UserRole)}
                  data-testid="ucw-role"
                >
                  {USER_ROLES.map((r) => (
                    <option key={r} value={r}>{roleLabels[r]}</option>
                  ))}
                </select>
              </div>
              {/* Organization: unavailable — show pending-contract info, no fake data */}
              <div
                className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm"
                data-testid="ucw-org-unavailable"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">{t('admin_users:wizard.org_unavailable_title')}</div>
                  <div>{t('admin_users:wizard.org_unavailable_msg')}</div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Security policy (replaces password collection) */}
          {step === 2 && (
            <div
              role="region"
              aria-labelledby={securityHeadingId}
              className="space-y-4 outline-none"
              data-testid="ucw-panel-access"
            >
              <div className="flex items-start gap-2 p-4 rounded-lg bg-[var(--color-info)]/10 border border-[var(--color-info)]/30 text-sm text-[var(--text-primary)]">
                <KeyRound className="w-5 h-5 mt-0.5 shrink-0 text-[var(--color-info)]" />
                <div>
                  <h3
                    id={securityHeadingId}
                    ref={accessHeadingRef}
                    tabIndex={-1}
                    className="font-medium outline-none"
                  >
                    {t('admin_users:wizard.security_policy_title')}
                  </h3>
                  <p className="text-[var(--text-secondary)] mt-1">{t('admin_users:wizard.security_policy_msg')}</p>
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 rounded-lg bg-[var(--color-info)]/10 border border-[var(--color-info)]/30 text-sm text-[var(--text-primary)]">
                <Shield className="w-4 h-4 mt-0.5 shrink-0 text-[var(--color-info)]" />
                <div>
                  <p>{t('admin_users:wizard.must_change_note')}</p>
                  <p className="text-[var(--text-secondary)] mt-1">{t('admin_users:wizard.twofa_note')}</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div
              role="region"
              aria-labelledby={reviewHeadingId}
              className="space-y-3 outline-none"
              data-testid="ucw-panel-review"
            >
              <h3
                id={reviewHeadingId}
                ref={reviewHeadingRef}
                tabIndex={-1}
                className="text-heading-3 text-[var(--text-primary)] outline-none"
              >
                {t('admin_users:wizard.step_review')}
              </h3>
              <p className="text-sm text-[var(--text-secondary)]">{t('admin_users:wizard.review_intro')}</p>
              <dl className="divide-y divide-[var(--border-light)] border border-[var(--border-light)] rounded-lg">
                {[
                  { id: 'name', label: t('admin_users:wizard.field_name'), value: form.name || '—' },
                  { id: 'email', label: t('admin_users:wizard.field_email'), value: form.email || '—' },
                  { id: 'role', label: t('admin_users:wizard.field_role'), value: roleLabels[form.role] },
                ].map(({ id, label, value }) => (
                  <div key={id} className="flex justify-between px-4 py-2 text-sm">
                    <dt className="text-[var(--text-secondary)]">{label}</dt>
                    <dd className="text-[var(--text-primary)] font-medium">{value}</dd>
                  </div>
                ))}
                {/* Organization: explicitly unavailable */}
                <div className="flex justify-between px-4 py-2 text-sm" data-testid="ucw-review-org-unavailable">
                  <dt className="text-[var(--text-secondary)]">{t('admin_users:wizard.field_org')}</dt>
                  <dd className="text-amber-700 font-medium text-xs text-right max-w-[50%]">
                    {t('admin_users:wizard.review_org_unavailable')}
                  </dd>
                </div>
                {/* Password: explicitly unavailable */}
                <div className="flex justify-between px-4 py-2 text-sm" data-testid="ucw-review-credentials-unavailable">
                  <dt className="text-[var(--text-secondary)]">{t('admin_users:wizard.field_password')}</dt>
                  <dd className="text-amber-700 font-medium text-xs text-right max-w-[50%]">
                    {t('admin_users:wizard.review_credentials_unavailable')}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {/* Step 4: Unavailable conclusion */}
          {step === STEP_COUNT - 1 && (
            <div className="text-center py-4" data-testid="ucw-conclusion">
              <AlertTriangle className="w-14 h-14 text-amber-500 mx-auto mb-3" />
              <h3
                ref={conclusionHeadingRef}
                tabIndex={-1}
                className="text-heading-3 text-[var(--text-primary)] outline-none"
              >
                {t('admin_users:wizard.conclusion_unavailable_title')}
              </h3>
              <p className="text-[var(--text-secondary)] text-sm mt-1 max-w-md mx-auto">
                {t('admin_users:wizard.conclusion_unavailable_msg')}
              </p>
              <p className="text-xs text-[var(--text-tertiary)] mt-4">
                {t('admin_users:wizard.conclusion_contract_ref')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-[var(--border-light)]">
          {step < STEP_COUNT - 1 ? (
            <>
              <button
                onClick={step === 0 ? close : back}
                className="flex items-center gap-1 px-4 py-2 text-[var(--text-secondary)] border border-[var(--border-light)] rounded-lg hover:bg-[var(--gray-50)]"
                data-testid="ucw-back"
              >
                {step === 0 ? t('common:cancel') : (<><ChevronLeft className="w-4 h-4" /> {t('admin_users:wizard.back')}</>)}
              </button>

              <button
                onClick={next}
                className="flex items-center gap-1 px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90"
                data-testid="ucw-next"
              >
                {t('admin_users:wizard.next')} <ChevronRight className="w-4 h-4" />
              </button>
            </>
          ) : (
            <button
              onClick={close}
              className="ml-auto flex items-center gap-1 px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90"
              data-testid="ucw-done"
            >
              {t('admin_users:wizard.done')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserCreationWizard;
